import { DepositPaymentMode } from './deposit-payment-mode';

export enum DepositCaptureStrategy {
  ChargeOnBookingRequest = 'chargeOnBookingRequest',
  CaptureOnBookingRequestApproval = 'captureOnBookingRequestApproval',
  CaptureOnCancellationOrReschedule = 'captureOnCancellationOrReschedule',
}

/**
 * A select option rendered as a title line plus a secondary description line,
 * so the user reads the consequence of each choice *before* choosing rather
 * than a hint that only describes what they already picked.
 */
export interface IDepositOptionText {
  /** The choice, phrased for whoever is picking it in settings. */
  readonly label: string;
  /**
   * The same concept phrased for staff reading a deposit that already exists,
   * where {@link label} reads as an instruction rather than a description.
   * Optional: omit it when the two readings coincide.
   */
  readonly staffLabel?: string;
  readonly description: string;
}

/**
 * A capture strategy describes *when* we take the money; a payment mode
 * describes *how* the payment intent must be raised with the payment provider.
 * Several strategies defer the capture and therefore share a single mode, so
 * this collapses the richer scheduling concept down to the provider-facing one.
 */
export const DEPOSIT_CAPTURE_STRATEGY_TO_PAYMENT_MODE_MAP: Record<
  DepositCaptureStrategy,
  DepositPaymentMode
> = {
  [DepositCaptureStrategy.ChargeOnBookingRequest]: DepositPaymentMode.Charge,
  [DepositCaptureStrategy.CaptureOnBookingRequestApproval]:
    DepositPaymentMode.PreAuth,
  [DepositCaptureStrategy.CaptureOnCancellationOrReschedule]:
    DepositPaymentMode.PreAuth,
};

export const DEPOSIT_CAPTURE_STRATEGY_TEXT: Record<
  DepositCaptureStrategy,
  IDepositOptionText
> = {
  [DepositCaptureStrategy.ChargeOnBookingRequest]: {
    label: 'Charge straight away',
    description:
      'The card is charged as soon as the booking is made, whether the patient booked online or your team made the booking.',
  },
  [DepositCaptureStrategy.CaptureOnBookingRequestApproval]: {
    label: 'Charge when you accept the booking',
    staffLabel: 'Hold, then charge when you accept',
    description:
      'Online requests hold the card and charge it when you accept. Bookings your team makes are charged straight away.',
  },
  [DepositCaptureStrategy.CaptureOnCancellationOrReschedule]: {
    label: 'Hold the card as cancellation cover',
    staffLabel: 'Hold as cancellation cover',
    description:
      'The card is held, then released when the patient attends. You only take the money if they cancel or reschedule late. A hold lasts 6 days, so bookings too far ahead use a fallback: online requests hold the card and charge when you accept, while bookings your team makes charge straight away.',
  },
};
